import { useState } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface EditablePriceProps {
  value: number | null;
  onSave: (value: number) => Promise<void>;
  className?: string;
  disabled?: boolean;
}

export const EditablePrice = ({
  value,
  onSave,
  className,
  disabled = false,
}: EditablePriceProps) => {
  const { isEditMode } = useEditMode();
  const { isEditor } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value?.toString() || '0');
  const [isSaving, setIsSaving] = useState(false);

  if (!isEditor || !isEditMode || disabled) {
    if (!value || value === 0) {
      return <span className={className}>Custom pricing</span>;
    }
    return <span className={className}>${value}</span>;
  }

  const handleSave = async () => {
    const numValue = parseFloat(editValue);
    if (isNaN(numValue) || numValue < 0) {
      alert('Please enter a valid price');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(numValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value?.toString() || '0');
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="relative inline-block">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold">$</span>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-32"
            autoFocus
          />
        </div>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Check className="h-4 w-4 mr-1" />
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative inline-block">
      <span
        className={cn(
          'border-2 border-dashed border-transparent hover:border-orange-300 hover:bg-orange-50/10 transition-all cursor-pointer rounded px-2 py-1',
          className
        )}
        onClick={() => setIsEditing(true)}
      >
        {!value || value === 0 ? 'Custom pricing' : `$${value}`}
      </span>
      <Button
        size="icon"
        variant="ghost"
        className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 bg-orange-500 hover:bg-orange-600 text-white"
        onClick={() => setIsEditing(true)}
      >
        <Pencil className="h-3 w-3" />
      </Button>
    </div>
  );
};
